import type { FileKind } from '../types';
import {
  DOCUMENT_NAME_MAX_LENGTH,
  truncateDocumentName,
} from '@/helpers/document-name';

const GENERATED_FILE_NAME_REGEX = /^[0-9a-f-]{36}-\d+-(.+)$/i;

export const getDisplayName = (rawName: string): string => {
  const m = rawName.match(GENERATED_FILE_NAME_REGEX);
  return m?.[1] ?? rawName;
};

export const getExt = (name: string): string =>
  name.split('.').pop()?.toLowerCase() ?? '';

export const getFileNameParts = (
  name: string
): { baseName: string; extension: string } => {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex <= 0) return { baseName: name, extension: '' };

  return {
    baseName: name.slice(0, dotIndex),
    extension: name.slice(dotIndex),
  };
};

const COPY_NUMBER_SUFFIX_REGEX = /^(.*)\((\d+)\)$/;

export const buildIncrementedCopyName = (
  name: string,
  existingNames: ReadonlySet<string>
): string => {
  const { baseName, extension } = getFileNameParts(name);
  const match = baseName.match(COPY_NUMBER_SUFFIX_REGEX);
  const rootBaseName = match ? match[1] : baseName;
  const buildCandidate = (number: number) => {
    const suffix = `(${number})`;
    const copyBaseName = truncateDocumentName(
      rootBaseName,
      DOCUMENT_NAME_MAX_LENGTH - suffix.length
    );
    return `${copyBaseName}${suffix}${extension}`;
  };

  let n = 2;
  let candidate = buildCandidate(n);
  while (existingNames.has(candidate)) {
    n += 1;
    candidate = buildCandidate(n);
  }
  return candidate;
};

export const canEditDocument = (kind: FileKind): boolean => kind === 'pdf';

export const getKindAndBadge = (
  name: string,
  mimeType: string
): { kind: FileKind; badgeLabel: string; badgeColor: string } => {
  const ext = getExt(name);

  if (mimeType.includes('pdf') || ext === 'pdf')
    return { kind: 'pdf', badgeLabel: 'PDF', badgeColor: '#f43f3f' };

  if (
    mimeType.includes('zip') ||
    mimeType.includes('x-rar') ||
    mimeType.includes('x-7z') ||
    mimeType.includes('x-tar') ||
    ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)
  )
    return { kind: 'zip', badgeLabel: 'ZIP', badgeColor: '#454655' };

  if (
    mimeType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(ext)
  )
    return {
      kind: 'image',
      badgeLabel: ext.toUpperCase() || 'IMG',
      badgeColor: '#787887',
    };

  if (
    mimeType.includes('spreadsheet') ||
    ['xlsx', 'xls', 'csv', 'ods'].includes(ext)
  )
    return { kind: 'office', badgeLabel: 'XLSX', badgeColor: '#1bcf63' };

  if (
    mimeType.includes('wordprocessingml') ||
    mimeType === 'application/msword' ||
    ['doc', 'docx', 'odt', 'rtf'].includes(ext)
  )
    return { kind: 'office', badgeLabel: 'DOCX', badgeColor: '#4b7eff' };

  if (
    mimeType.includes('presentation') ||
    ['ppt', 'pptx', 'odp'].includes(ext)
  )
    return { kind: 'office', badgeLabel: 'PPTX', badgeColor: '#ff7b54' };

  return {
    kind: 'other',
    badgeLabel: ext.toUpperCase() || 'FILE',
    badgeColor: '#787887',
  };
};
